import * as types from 'src/store/mutation-types'
import { CompanyClient } from 'src/api/Companies'
import { CustomersClient } from 'src/api/Customers'
import { NoteClient } from 'src/api/Notes'
import { Notify } from 'quasar'
import { PersonClient } from 'src/api/Persons'
import { createErrorFromResponse } from 'src/utils/error'
import i18n from 'src/i18n/i18n'

// initial state
// shape: [{ id, quantity }]
const state = {
  customers: [],
  customersFilter: [],
  customersMeta: null,
  customersLoaded: false,
  selectedCustomer: null,
  checkedCustomers: [],
  customerModalData: null,
  customerExportModalOpened: false
}

// getters
const getters = {
  customers: state => state.customers,
  customersMeta: state => state.customersMeta,
  customersFilter: state => state.customersFilter,
  customersLoaded: state => state.customersLoaded,
  selectedCustomer: state => state.selectedCustomer,
  checkedCustomers: state => state.checkedCustomers,
  customerModalData: state => state.customerModalData,
  customerExportModalOpened: state => state.customerExportModalOpened,
  customerProjects: (state, getters, rootState, rootGetters) => getters.selectedCustomer
    ? rootGetters.projects.filter(project => project.customer_id === getters.selectedCustomer.id)
    : []
}

const getClient = type => (type === 'person') ? new PersonClient() : new CompanyClient()

const actions = {
  fetchCustomersOverview ({ commit }) {
    return new CustomersClient()
      .filter(state.customersFilter)
      .sort('name:asc')
      .include(['default_email_address', 'default_phone_number', 'company'])
      .list()
      .then(response => {
        commit(types.SET_CUSTOMERS, response.data.data)
        commit(types.SET_CUSTOMERS_META, response.data.meta)
        commit(types.SET_CUSTOMERS_LOADED, true)
      })
  },

  fetchSelectedCustomer ({ commit, dispatch }, customerId) {
    return dispatch('fetchCustomer', customerId)
      .then(response => {
        const data = response.data.data[0]
        commit(types.SET_SELECTED_CUSTOMER, data)
        if (data.projects) {
          commit(types.SET_PROJECTS, data.projects.data)
        }
      })
  },

  fetchCustomer ({ commit }, customerId) {
    return new CustomersClient()
      .include([
        'default_email_address',
        'default_phone_number',
        'email_addresses',
        'addresses',
        'phone_numbers',
        'notes',
        'notes.user',
        'company',
        'invoices',
        'quotes',
        'statistics',
        'projects.statistics',
        'default_bank_details',
        'bank_details',
        'contact_persons'
      ])
      .sort('notes.id:asc,invoices.invoice_date:desc')
      .get(customerId)
  },

  deselectCustomer ({ commit }) {
    return commit(types.SET_SELECTED_CUSTOMER, null)
  },

  fetchNextCustomersChunk ({ commit, state }, done) {
    if (!state.customersLoaded) {
      return done()
    } else if (!state.customersMeta) {
      return done(true)
    }

    let currentPage = state.customersMeta.pagination.current_page
    let totalPages = state.customersMeta.pagination.total_pages

    // Check if there is still a chunk to fetch available.
    if (currentPage < totalPages) {
      new CustomersClient()
        .page(currentPage + 1)
        .filter(state.customersFilter)
        .sort('name:asc')
        .include(['default_email_address', 'default_phone_number', 'company'])
        .list()
        .then(response => {
          let newCustomers = state.customers.concat(response.data.data)

          commit(types.SET_CUSTOMERS, newCustomers)
          commit(types.SET_CUSTOMERS_META, response.data.meta)

          // Indicate that data has been loaded successfully.
          done()
        })
    } else {
      // No more data available to fetch.
      // Passing true to the callback function tells the infinite scroll component to stop loading.
      done(true)
    }
  },

  checkCustomer ({ commit, state }, { customer, value }) {
    if (!state.checkedCustomers.includes(customer) && value) {
      commit(types.ADD_CHECKED_CUSTOMER, customer)
    } else if (state.checkedCustomers.includes(customer) && !value) {
      commit(types.REMOVE_CHECKED_CUSTOMER, customer)
    }
  },

  clearCheckedCustomers ({ commit }) {
    commit(types.CLEAR_CHECKED_CUSTOMERS)
  },

  deleteCustomer ({ commit }, { customerId, type }) {
    return getClient(type)
      .destroy(customerId)
      .then(() => {
        commit(types.REMOVE_CUSTOMER, customerId)
        commit(types.CLEAR_CHECKED_CUSTOMERS)
      })
      .then(() => {
        Notify.create({ message: i18n.tc(`${type}_deleted`, 1), type: 'positive' })
      })
      .catch(createErrorFromResponse)
  },

  deleteCheckedCustomers ({ commit, state }) {
    const persons = state.checkedCustomers
      .filter(customer => customer.type === 'person')
      .map(customer => customer.id)

    const company = state.checkedCustomers
      .filter(customer => customer.type === 'company')
      .map(customer => customer.id)

    let personPromise, companyPromise
    if (persons.length > 0) {
      personPromise = new PersonClient()
        .destroy(persons)
        .catch(createErrorFromResponse)
    }

    if (company.length > 0) {
      companyPromise = new CompanyClient()
        .destroy(company)
        .catch(createErrorFromResponse)
    }

    return Promise.all([companyPromise, personPromise])
      .then(() => {
        commit(types.REMOVE_CUSTOMERS, persons.concat(company))
        commit(types.CLEAR_CHECKED_CUSTOMERS)

        Notify.create({ message: i18n.t('partners_deleted'), type: 'positive' })
      })
  },

  createCustomerAddress ({ commit }, { customerId, data, type }) {
    return getClient(type)
      .createAddress(customerId, data)
      .then(response => {
        let address = response.data.data

        commit(types.CREATE_CUSTOMER_ADDRESS, { customerId, address })
      })
  },

  updateCustomerAddress ({ commit }, { customerId, addressId, address, type }) {
    return getClient(type)
      .updateAddress(customerId, addressId, address)
      .then(response => {
        let address = response.data.data

        commit(types.UPDATE_CUSTOMER_ADDRESS, { customerId, addressId, address })
      })
  },

  deleteCustomerAddress ({ commit }, { customerId, addressId, type }) {
    return getClient(type)
      .destroyAddresses(customerId, addressId)
      .then(response => {
        commit(types.REMOVE_CUSTOMER_ADDRESS, { customerId, addressId })
      })
  },

  createCustomerEmailAddress ({ commit }, { customerId, data, type }) {
    return getClient(type)
      .createEmailAddress(customerId, data)
      .then(response => {
        let emailAddress = response.data.data

        commit(types.CREATE_CUSTOMER_EMAIL_ADDRESS, { customerId, emailAddress })
      })
  },

  updateCustomerEmailAddress ({ commit }, { customerId, emailAddressId, emailAddress, type }) {
    return getClient(type)
      .updateEmailAddress(customerId, emailAddressId, emailAddress)
      .then(response => {
        let emailAddress = response.data.data

        commit(types.UPDATE_CUSTOMER_EMAIL_ADDRESS, { customerId, emailAddressId, emailAddress })
      })
  },

  deleteCustomerEmailAddress ({ commit }, { customerId, emailAddressId, type }) {
    return getClient(type)
      .destroyEmailAddresses(customerId, emailAddressId)
      .then(response => {
        commit(types.REMOVE_CUSTOMER_EMAIL_ADDRESS, { customerId, emailAddressId })
      })
  },

  createCustomerPhoneNumber ({ commit }, { customerId, data, type }) {
    return getClient(type)
      .createPhoneNumber(customerId, data)
      .then(response => {
        let phoneNumber = response.data.data

        commit(types.CREATE_CUSTOMER_PHONE_NUMBER, { customerId, phoneNumber })
      })
  },

  updateCustomerPhoneNumber ({ commit }, { customerId, phoneNumberId, phoneNumber, type }) {
    return getClient(type)
      .updatePhoneNumber(customerId, phoneNumberId, phoneNumber)
      .then(response => {
        let phoneNumber = response.data.data

        commit(types.UPDATE_CUSTOMER_PHONE_NUMBER, { customerId, phoneNumberId, phoneNumber })
      })
  },

  deleteCustomerPhoneNumber ({ commit }, { customerId, phoneNumberId, type }) {
    return getClient(type)
      .destroyPhoneNumbers(customerId, phoneNumberId)
      .then(response => {
        commit(types.REMOVE_CUSTOMER_PHONE_NUMBER, { customerId, phoneNumberId })
      })
  },

  createCustomerNote ({ commit }, { customerId, data, type }) {
    return getClient(type)
      .include(['user'])
      .createNote(customerId, data)
      .then(response => {
        let note = response.data.data

        commit(types.CREATE_CUSTOMER_NOTE, { customerId, note })
      })
  },

  updateCustomerNote ({ commit }, { customerId, noteId, note }) {
    return new NoteClient()
      .include(['user'])
      .update(noteId, note)
      .then(response => {
        let note = response.data.data

        commit(types.UPDATE_CUSTOMER_NOTE, { customerId, noteId, note })
      })
  },

  deleteCustomerNote ({ commit }, { customerId, noteId }) {
    return new NoteClient()
      .destroy(noteId)
      .then(response => {
        commit(types.REMOVE_CUSTOMER_NOTE, { customerId, noteId })
      })
  },

  createCustomerBankDetails ({ commit }, { customerId, data, type }) {
    return getClient(type)
      .createBankDetails(customerId, data)
      .then(response => {
        let bankDetails = response.data.data

        commit(types.CREATE_CUSTOMER_BANK_DETAILS, { customerId, bankDetails })
      })
  },

  updateCustomerBankDetails ({ commit }, { customerId, bankDetailsId, bankDetails, type }) {
    return getClient(type)
      .updateBankDetails(customerId, bankDetailsId, bankDetails)
      .then(response => {
        let bankDetails = response.data.data

        commit(types.UPDATE_CUSTOMER_BANK_DETAILS, { customerId, bankDetailsId, bankDetails })
      })
  },

  deleteCustomerBankDetails ({ commit }, { customerId, bankDetailsId, type }) {
    return getClient(type)
      .destroyBankDetails(customerId, bankDetailsId)
      .then(response => {
        commit(types.REMOVE_CUSTOMER_BANK_DETAILS, { customerId, bankDetailsId })
      })
  },

  filterCustomerType ({ commit, dispatch }, type) {
    commit(types.SET_CUSTOMERS_FILTER, type)

    return dispatch('fetchCustomersOverview')
  },

  setOpenEditCustomerModal ({ commit }, data) {
    commit(types.OPEN_EDIT_CUSTOMER_MODAL, data)
  },

  openCustomerExportModal ({ commit }) {
    commit(types.SET_EXPORT_MODAL_STATUS, true)
  },

  closeCustomerExportModal ({ commit }) {
    commit(types.SET_EXPORT_MODAL_STATUS, false)
  },

  createContactPerson ({ commit }, { customerId, person }) {
    return new CompanyClient()
      .createContactPerson(customerId, person)
      .then(createdPerson => {
        commit(types.ADD_CONTACT_PERSON, { customerId, person: createdPerson.data.data })

        return createdPerson.data.data
      })
      .catch(createErrorFromResponse)
  },

  updateContactPerson ({ commit }, { customerId, person }) {
    return new CompanyClient()
      .updateContactPerson(customerId, person.id, person)
      .then(updatedPerson => {
        commit(types.UPDATE_CONTACT_PERSON, { customerId, person: updatedPerson.data.data })
      })
      .catch(createErrorFromResponse)
  },

  removeContactPerson ({ commit }, { customerId, personId }) {
    return new CompanyClient()
      .deleteContactPerson(customerId, personId)
      .then(() => {
        commit(types.REMOVE_CONTACT_PERSON, { customerId, personId })
      })
      .catch(createErrorFromResponse)
  }
}

// mutations
const mutations = {
  [types.SET_CUSTOMERS] (state, customers) {
    state.customers = customers
  },

  [types.SET_CUSTOMERS_META] (state, meta) {
    state.customersMeta = meta
  },

  [types.SET_CUSTOMERS_LOADED] (state, value) {
    state.customersLoaded = value
  },

  [types.SET_SELECTED_CUSTOMER] (state, customer) {
    state.selectedCustomer = customer
  },

  [types.ADD_CHECKED_CUSTOMER] (state, customer) {
    state.checkedCustomers.push(customer)
  },

  [types.ADD_CUSTOMER] (state, customer) {
    state.customers.push(customer)
  },

  [types.UPDATE_CUSTOMER] (state, updatedCustomer) {
    let customerIndex = state.customers.findIndex(customer => customer.id === updatedCustomer.id)
    state.customers.splice(customerIndex, 1, updatedCustomer)

    if (state.selectedCustomer && state.selectedCustomer.id === updatedCustomer.id) {
      state.selectedCustomer = Object.assign({}, state.selectedCustomer, updatedCustomer)
    }
  },

  [types.CREATE_CUSTOMER_ADDRESS] (state, { customerId, address }) {
    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      if (!state.selectedCustomer.addresses) {
        // Replace entire object to maintain reactivity
        state.selectedCustomer = { ...state.selectedCustomer, addresses: { data: [] } }
      }

      state.selectedCustomer.addresses.data.push(address)
    }
  },

  [types.UPDATE_CUSTOMER_ADDRESS] (state, { customerId, addressId, address }) {
    let customerIndex = state.customers.findIndex(customer => customer.id === customerId)
    let customer = state.customers[customerIndex]

    if (customer.default_address && customer.default_address.data.id === addressId) {
      customer.default_address.data = address
    }

    if (state.selectedCustomer && state.selectedCustomer.addresses) {
      let addressIndex = state.selectedCustomer.addresses.data.findIndex(address => address.id === addressId)
      state.selectedCustomer.addresses.data.splice(addressIndex, 1, address)
    }
  },

  [types.REMOVE_CUSTOMER_ADDRESS] (state, { customerId, addressId }) {
    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      let addressIndex = state.selectedCustomer.addresses.data.findIndex(address => address.id === addressId)
      state.selectedCustomer.addresses.data.splice(addressIndex, 1)
    }
  },

  [types.CREATE_CUSTOMER_EMAIL_ADDRESS] (state, { customerId, emailAddress }) {
    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      if (!state.selectedCustomer.email_addresses) {
        // Replace entire object to maintain reactivity
        state.selectedCustomer = { ...state.selectedCustomer, email_addresses: { data: [] } }
      }

      state.selectedCustomer.email_addresses.data.push(emailAddress)
    }
  },

  [types.UPDATE_CUSTOMER_EMAIL_ADDRESS] (state, { customerId, emailAddressId, emailAddress }) {
    let customerIndex = state.customers.findIndex(customer => customer.id === customerId)
    let customer = state.customers[customerIndex]

    if (customer.default_email_address && customer.default_email_address.data.id === emailAddressId) {
      customer.default_email_address.data = emailAddress
    }

    if (state.selectedCustomer.addresses) {
      let emailAddressIndex = state.selectedCustomer.email_addresses.data.findIndex(emailAddress => emailAddress.id === emailAddressId)
      state.selectedCustomer.email_addresses.data.splice(emailAddressIndex, 1, emailAddress)
    }
  },

  [types.REMOVE_CUSTOMER_EMAIL_ADDRESS] (state, { customerId, emailAddressId }) {
    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      let emailAddressIndex = state.selectedCustomer.email_addresses.data.findIndex(emailAddress => emailAddress.id === emailAddressId)
      state.selectedCustomer.email_addresses.data.splice(emailAddressIndex, 1)
    }
  },

  [types.CREATE_CUSTOMER_PHONE_NUMBER] (state, { customerId, phoneNumber }) {
    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      if (!state.selectedCustomer.phone_numbers) {
        // Replace entire object to maintain reactivity
        state.selectedCustomer = { ...state.selectedCustomer, phone_numbers: { data: [] } }
      }

      state.selectedCustomer.phone_numbers.data.push(phoneNumber)
    }
  },

  [types.UPDATE_CUSTOMER_PHONE_NUMBER] (state, { customerId, phoneNumberId, phoneNumber }) {
    let customerIndex = state.customers.findIndex(customer => customer.id === customerId)
    let customer = state.customers[customerIndex]

    if (customer.default_phone_number && customer.default_phone_number.data.id === phoneNumberId) {
      customer.default_phone_number.data = phoneNumber
    }

    if (state.selectedCustomer.phone_numbers) {
      let phoneNumberIndex = state.selectedCustomer.phone_numbers.data.findIndex(phoneNumber => phoneNumber.id === phoneNumberId)
      state.selectedCustomer.phone_numbers.data.splice(phoneNumberIndex, 1, phoneNumber)
    }
  },

  [types.REMOVE_CUSTOMER_PHONE_NUMBER] (state, { customerId, phoneNumberId }) {
    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      let phoneNumberIndex = state.selectedCustomer.phone_numbers.data.findIndex(phoneNumber => phoneNumber.id === phoneNumberId)
      state.selectedCustomer.phone_numbers.data.splice(phoneNumberIndex, 1)
    }
  },

  [types.CREATE_CUSTOMER_NOTE] (state, { customerId, note }) {
    let customerIndex = state.customers.findIndex(customer => customer.id === customerId)
    if (customerIndex !== -1 && state.customers[customerIndex].notes) {
      state.customers[customerIndex].notes.data.push(note)
    }

    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      if (!state.selectedCustomer.notes) {
        // Replace entire object to maintain reactivity
        state.selectedCustomer = { ...state.selectedCustomer, notes: { data: [] } }
      }

      state.selectedCustomer.notes.data.push(note)
    }
  },

  [types.UPDATE_CUSTOMER_NOTE] (state, { customerId, noteId, note }) {
    let customerIndex = state.customers.findIndex(customer => customer.id === customerId)
    let customer = state.customers[customerIndex]
    if (customer.notes) {
      let noteIndex = customer.notes.data.findIndex(note => note.id === noteId)
      customer.notes.data.splice(noteIndex, 1, note)
    }

    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      let noteIndex = state.selectedCustomer.notes.data.findIndex(note => note.id === noteId)
      state.selectedCustomer.notes.data.splice(noteIndex, 1, note)
    }
  },

  [types.REMOVE_CUSTOMER_NOTE] (state, { customerId, noteId }) {
    let customerIndex = state.customers.findIndex(customer => customer.id === customerId)
    if (state.customers[customerIndex].notes) {
      let noteIndex = state.customers[customerIndex].notes.data.findIndex(note => note.id === noteId)
      state.customers[customerIndex].notes.splice(noteIndex, 1)
    }

    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      let noteIndex = state.selectedCustomer.notes.data.findIndex(note => note.id === noteId)
      state.selectedCustomer.notes.data.splice(noteIndex, 1)
    }
  },

  [types.CREATE_CUSTOMER_BANK_DETAILS] (state, { customerId, bankDetails }) {
    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      if (!state.selectedCustomer.bank_details) {
        // Replace entire object to maintain reactivity
        state.selectedCustomer = { ...state.selectedCustomer, bank_details: { data: [] } }
      }

      state.selectedCustomer.bank_details.data.push(bankDetails)
    }
  },

  [types.UPDATE_CUSTOMER_BANK_DETAILS] (state, { customerId, bankDetailsId, bankDetails }) {
    let customerIndex = state.customers.findIndex(customer => customer.id === customerId)
    let customer = state.customers[customerIndex]

    if (customer.default_bank_details && customer.default_bank_details.data.id === bankDetailsId) {
      customer.default_bank_details.data = bankDetails
    }

    if (state.selectedCustomer.addresses) {
      let bankDetailsIndex = state.selectedCustomer.bank_details.data.findIndex(bankDetails => bankDetails.id === bankDetailsId)
      state.selectedCustomer.bank_details.data.splice(bankDetailsIndex, 1, bankDetails)
    }
  },

  [types.REMOVE_CUSTOMER_BANK_DETAILS] (state, { customerId, bankDetailsId }) {
    if (state.selectedCustomer && state.selectedCustomer.id === customerId) {
      let bankDetailsIndex = state.selectedCustomer.bank_details.data.findIndex(bankDetails => bankDetails.id === bankDetailsId)
      state.selectedCustomer.bank_details.data.splice(bankDetailsIndex, 1)
    }
  },

  [types.REMOVE_CUSTOMER] (state, customerId) {
    const index = state.customers.findIndex(customer => customer.id === customerId)
    state.customers.splice(index, 1)
  },

  [types.REMOVE_CUSTOMERS] (state, ids) {
    ids.forEach(customerId => {
      const index = state.customers.findIndex(customer => customer.id === customerId)
      state.customers.splice(index, 1)
    })
  },

  [types.REMOVE_CHECKED_CUSTOMER] (state, uncheck) {
    const index = state.checkedCustomers.findIndex(customer => customer.id === uncheck.id)

    if (index !== -1) {
      state.checkedCustomers.splice(index, 1)
    }
  },

  [types.CLEAR_CHECKED_CUSTOMERS] (state) {
    state.checkedCustomers = []
  },

  [types.SET_CUSTOMERS_FILTER] (state, filter) {
    const filters = {
      both: [],
      person: ['type', '=', 'person'],
      company: ['type', '=', 'company']
    }

    if (!filters[filter]) throw new Error('Invalid customer filter')

    state.customersFilter = filters[filter]
  },

  [types.OPEN_EDIT_CUSTOMER_MODAL] (state, data) {
    state.customerModalData = data
  },

  [types.SET_EXPORT_MODAL_STATUS] (state, opened) {
    state.customerExportModalOpened = opened
  },

  [types.ADD_CONTACT_PERSON] (state, { customerId, person }) {
    if (!state.selectedCustomer || customerId !== state.selectedCustomer.id) {
      return
    }

    if (state.selectedCustomer.contact_persons) {
      state.selectedCustomer.contact_persons.data.push(person)
    } else {
      state.selectedCustomer.contact_persons = { data: [person] }
    }
  },

  [types.UPDATE_CONTACT_PERSON] (state, { customerId, person }) {
    if (!state.selectedCustomer || customerId !== state.selectedCustomer.id || !state.selectedCustomer.contact_persons) {
      console.log('no selected customer')
      return
    }

    const personId = person.id
    const persons = state.selectedCustomer.contact_persons.data
    const personIndex = persons.findIndex(person => person.id === personId)
    if (personIndex === -1) {
      return
    }

    state.selectedCustomer.contact_persons.data.splice(personIndex, 1, person)
  },

  [types.REMOVE_CONTACT_PERSON] (state, { customerId, personId }) {
    if (!state.selectedCustomer || customerId !== state.selectedCustomer.id || !state.selectedCustomer.contact_persons) {
      return
    }

    const persons = state.selectedCustomer.contact_persons.data
    const personIndex = persons.findIndex(person => person.id === personId)
    if (personIndex === -1) {
      return
    }

    state.selectedCustomer.contact_persons.data.splice(personIndex, 1)
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
